import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FeedbackBar from './FeedbackBar';
import FollowUpChips from './FollowUpChips';

/* ── Inline source row ─────────────────────────────────────────────── */
function InlineSource({ source }) {
  const url =
    source.sharepointUrl || source.websiteUrl || '';

  const label = source.sharepointUrl
    ? `SharePoint: ${source.title || source.category
    }`
    : source.title || source.category;

  if (!url) return null;

  return (
    <div className="flex items-center gap-1.5 mt-1">
      <span style={{ fontSize: '13px' }}></span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#fff',
          fontSize: '13px',
          textDecoration: 'underline',
        }}
      >
        {label}
      </a>
    </div>
  );
}

/* ── Main bubble ───────────────────────────────────────────────────── */
function MessageBubble({ message, onFollowUp }) {
  const isUser = message.role === 'user';
  const hasSources =
    message.sources && message.sources.length > 0;

  /* ── User bubble ─────────────────────────────────────────────────── */
  if (isUser) {
    return (
      <div className="flex mb-4 justify-end">
        <div
          style={{
            maxWidth: '60%',
            background:
              'rgba(255,255,255,0.08)',
            border:
              '1px solid rgba(255,255,255,0.18)',
            color:
              'rgba(255,255,255,0.80)',
            borderRadius: '20px',
            padding: '10px 16px',
            fontSize: '14px',
            lineHeight: '1.6',
            fontWeight: 500,
            // borderRight: '6px solid #00fac2',
          }}
        >
          <p className="m-0 whitespace-pre-wrap">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  /* ── Assistant bubble ────────────────────────────────────────────── */
  return (
    <div className="flex mb-5 justify-start">
      <div
        style={{
          maxWidth: '86%',
          background: 'linear-gradient(264.28deg, #00fac2 -38.2%, #66c 103.12%)',
          color: '#ffffff',
          borderRadius: '4px 16px 16px 16px',
          padding: '18px 22px 14px 22px',
          border: '1px solid rgba(0,0,0,0.07)',
          borderLeft: '3px solid #fff',
          fontSize: '14px',
          lineHeight: '1.7',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}
      >
        {/* Query type badge */}
        {message.queryType && (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 10px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              background:
                'rgba(0,0,0,1)',
              border:
                '1px solid rgba(0,0,0,1)',
              color: '#00afc2',
              marginBottom: '12px',
            }}
          >
            {message.queryType}
          </span>
        )}

        {/* Markdown content */}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1
                style={{
                  fontSize: '17px',
                  fontWeight: 700,
                  color: '#fff',
                  margin: '14px 0 6px',
                }}
              >
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2
                style={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#fff',
                  margin: '12px 0 5px',
                }}
              >
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#fff',
                  margin: '10px 0 4px',
                }}
              >
                {children}
              </h3>
            ),
            p: ({ children }) => (
              <p
                style={{
                  margin: '0 0 8px',
                  color: '#fff',
                }}
              >
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul
                style={{
                  paddingLeft: '20px',
                  margin: '4px 0 10px',
                }}
              >
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol
                style={{
                  paddingLeft: '20px',
                  margin: '4px 0 10px',
                }}
              >
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li style={{ marginBottom: '4px' }}>
                {children}
              </li>
            ),
            strong: ({ children }) => (
              <strong
                style={{
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                {children}
              </strong>
            ),
            em: ({ children }) => (
              <em style={{ fontStyle: 'italic' }}>
                {children}
              </em>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#fff',
                  textDecoration: 'underline',
                }}
              >
                {children}
              </a>
            ),
            code: ({ children }) => (
              <code
                style={{
                  background:
                    'rgba(0,0,0,0.08)',
                  color: '#fff',
                  padding: '1px 5px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}
              >
                {children}
              </code>
            ),
            hr: () => (
              <hr
                style={{
                  border: 'none',
                  borderTop:
                    '1px solid rgba(0,0,0,0.08)',
                  margin: '12px 0',
                }}
              />
            ),
          }}
        >
          {message.content}
        </ReactMarkdown>

        {/* Sources */}
        {hasSources && (
          <div
            style={{
              marginTop: '12px',
              paddingTop: '10px',
              borderTop:
                '1px solid rgba(255,255,255,0.5)',
            }}
          >
            <p
              style={{
                fontSize: '11px',
                fontWeight: 600,
                background:
                  'rgba(0,0,0,1)',
                border:
                  '1px solid rgba(0,0,0,1)',
                color: '#00afc2',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginTop: '15px',
                marginBottom: '10px',
                width: 'fit-content',
                padding: '2px 10px',
                borderRadius: '4px',
              }}
            >
              Resources
            </p>
            {message.sources.map((s, i) => (
              <InlineSource key={i} source={s} />
            ))}
          </div>
        )}

        {/* Follow‑ups */}
        {message.followUps &&
          message.followUps.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <FollowUpChips
                chips={message.followUps}
                onSelect={onFollowUp}
              />
            </div>
          )}

        {/* Feedback */}
        <FeedbackBar messageId={message.id} />
      </div>
    </div>
  );
}

export default MessageBubble;
